import { Quaternion, Vector3, Euler, MathUtils } from 'three';

const SPEED_BASE = 80;
const SPEED_MIN_AIR = 40;     // stall speed in flight (≈144 km/h)
const SPEED_MIN_GROUND = 0;   // can fully stop on the runway
const SPEED_MAX = 6860;       // Mach 20 (≈ 24,696 km/h, sea-level a≈343 m/s)
export const ROTATE_SPEED_MS = 65; // Vr — 地上でこの速度(≈234km/h)未満は機首を上げても離陸できない
const THROTTLE_ACCEL = 40;    // 空中のみ。~170 s from base to Mach 20 at full throttle
const THROTTLE_RELAX = 0.5;
const BRAKE_DECEL = 100;      // 空中のみ。m/s² when brake held (≈10 s from Mach 1 to 0)
// --- 地上の縦力（空中はアーケードのまま、地上だけ実機相当に分離）---
const GROUND_THROTTLE_ACCEL = 3.0; // m/s² Shift地上: 0→Vr(65) ≈22s / ~700m の加速滑走
const GROUND_REVERSE_DECEL = 4.0;  // m/s² Ctrl/−ボタン地上（逆噴射相当）: 70→0 ≈17.5s
const GROUND_BRAKE_DECEL = 4.5;    // m/s² B地上（車輪ブレーキ）: 70→0 ≈15.6s / ~540m
const GROUND_FRICTION = 1.2;       // m/s² 転がり抵抗（無操作の惰性ロールアウト: 70→0 ≈58s / ~2km）
const YAW_RATE = 0.8;
const PITCH_RATE = 0.6;
const PITCH_LIMIT = MathUtils.degToRad(70);
const GROUND_PITCH_LIMIT = MathUtils.degToRad(8); // 地上滑走中の機首上げ上限（70°突っ立ち防止）
const ROTATE_AUTHORITY_MS = 55;   // ≈0.85·Vr — これ未満では昇降舵に機首を上げる力がない
const DEROTATE_RESPONSE = 3.0;    // /s 接地後に機首が水平へ沈む（デロテーション）指数レート
const NOSE_UP_CMD = -0.2;         // これより負のピッチ入力を「機首上げ操作中」とみなす閾値
const MAX_BANK = MathUtils.degToRad(45); // visual roll range (cosmetic only)
const BANK_RESPONSE = 5.0;               // higher = snappier bank response

const WORLD_UP = new Vector3(0, 1, 0);
const FORWARD = new Vector3(0, 0, 1);

export function createKeyboardInputProvider() {
  const keys = Object.create(null);
  let enabled = true;

  const onKeyDown = (e) => {
    // 無効化中（解説モーダル表示中など）は記録も preventDefault もしない。
    // ここで preventDefault を続けると、モーダル上の Space による決定や
    // 矢印キーによる本文スクロールまで潰してしまう。
    if (!enabled) return;
    keys[e.code] = true;
    if (
      e.code === 'ArrowUp' ||
      e.code === 'ArrowDown' ||
      e.code === 'ArrowLeft' ||
      e.code === 'ArrowRight' ||
      e.code === 'Space'
    ) {
      e.preventDefault();
    }
  };
  // キー離しは無効化中でも必ず記録する（押しっぱなしのまま無効化 →
  // モーダル上で離す、という順でも状態が残らないように）。
  const onKeyUp = (e) => { keys[e.code] = false; };
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return {
    read() {
      // 無効中は setEnabled が keys を空にし、keydown も記録しないので、
      // 通常の計算がそのまま全ゼロになる（別途のガードは要らない）。
      const yaw = (keys.ArrowLeft ? -1 : 0) + (keys.ArrowRight ? 1 : 0);
      const pitch = (keys.ArrowUp ? -1 : 0) + (keys.ArrowDown ? 1 : 0);
      const thr =
        ((keys.ShiftLeft || keys.ShiftRight) ? 1 : 0) +
        ((keys.ControlLeft || keys.ControlRight) ? -1 : 0);
      const brake = keys.KeyB ? 1 : 0;
      return { yaw, pitch, thr, brake };
    },
    setEnabled(on) {
      enabled = !!on;
      if (!enabled) for (const k in keys) keys[k] = false;
    },
    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    },
  };
}

export function createControls(aircraft, provider) {
  const inputProvider = provider ?? createKeyboardInputProvider();

  const state = { speed: SPEED_BASE, noseUpCommanded: false };

  const qYaw = new Quaternion();
  const qPitch = new Quaternion();
  const localX = new Vector3();
  const fwd = new Vector3();
  const eul = new Euler();

  function update(dt, ctx = {}) {
    if (!aircraft) return;
    const { yaw, pitch, thr, brake = 0 } = inputProvider.read();
    const onGround = !!ctx.onGround;

    if (brake > 0) {
      // 地上は車輪ブレーキ相当、空中は従来のエアブレーキ（アーケード）。
      state.speed -= (onGround ? GROUND_BRAKE_DECEL : BRAKE_DECEL) * dt;
    } else if (thr !== 0) {
      if (onGround) {
        // 地上は実機相当: ゆっくり加速滑走（0→Vr ≈22秒）、Ctrl は逆噴射。
        state.speed += (thr > 0 ? GROUND_THROTTLE_ACCEL : -GROUND_REVERSE_DECEL) * dt;
      } else {
        state.speed += thr * THROTTLE_ACCEL * dt;
      }
    } else if (onGround) {
      // 地上でスロットル中立 → 弱い転がり抵抗で長いロールアウト。
      state.speed -= GROUND_FRICTION * dt;
    } else {
      // In-flight only: throttle naturally settles back to cruise base.
      const a = 1 - Math.exp(-THROTTLE_RELAX * dt);
      state.speed += (SPEED_BASE - state.speed) * a;
    }
    const minSpeed = onGround ? SPEED_MIN_GROUND : SPEED_MIN_AIR;
    const maxSpeed = Math.min(SPEED_MAX, ctx.maxSpeed ?? SPEED_MAX);
    state.speed = Math.max(minSpeed, Math.min(maxSpeed, state.speed));

    // 「ローテーション操作中」= 権威速度以上で機首上げ入力を保持。下のデロテー
    // ション判定と、game.js の離陸グルー（車輪を離すか）が共にこれを参照する。
    state.noseUpCommanded =
      pitch < NOSE_UP_CMD && state.speed >= ROTATE_AUTHORITY_MS;

    if (yaw !== 0) {
      qYaw.setFromAxisAngle(WORLD_UP, -yaw * YAW_RATE * dt);
      aircraft.quaternion.premultiply(qYaw);
    }

    // ローテーション権威: 地上で速度不足のうちは昇降舵で機首を上げられない
    // （機首上げ = 負のピッチ入力。イージングだけだと入力の積分に負けるので入力自体を無効化）。
    let pitchIn = pitch;
    if (onGround && pitchIn < 0 && state.speed < ROTATE_AUTHORITY_MS) pitchIn = 0;
    if (pitchIn !== 0) {
      localX.set(1, 0, 0).applyQuaternion(aircraft.quaternion);
      qPitch.setFromAxisAngle(localX, pitchIn * PITCH_RATE * dt);
      aircraft.quaternion.premultiply(qPitch);
    }

    eul.setFromQuaternion(aircraft.quaternion, 'YXZ');
    if (onGround) {
      // ローテーション操作中以外は機首を水平へ滑らかに戻す。
      // 接地直後に機首がゆっくり沈むデロテーションもこれが担う。
      if (!state.noseUpCommanded) {
        const a = 1 - Math.exp(-DEROTATE_RESPONSE * dt);
        eul.x += (0 - eul.x) * a;
      }
      // 非対称クランプ（機首上げ = eul.x 負）: 上げは 8° まで、下げ（正）は禁止。
      eul.x = Math.min(0, Math.max(-GROUND_PITCH_LIMIT, eul.x));
    } else {
      eul.x = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, eul.x));
    }
    eul.z = 0;
    aircraft.quaternion.setFromEuler(eul);

    fwd.copy(FORWARD).applyQuaternion(aircraft.quaternion);
    aircraft.position.addScaledVector(fwd, state.speed * dt);

    // Visual coordinated bank: smoothly roll the inner container based on
    // yaw input. The chase camera looks toward +Z, which inverts screen-X
    // relative to world-X (camera local +X = world -X). Use a POSITIVE
    // sign so that turning right (yaw>0) makes the plane visually lean to
    // the right side of the screen.
    const rollContainer = aircraft.userData?.rollContainer;
    if (rollContainer) {
      const targetRoll = yaw * MAX_BANK;
      const a = 1 - Math.exp(-BANK_RESPONSE * dt);
      rollContainer.rotation.z += (targetRoll - rollContainer.rotation.z) * a;
    }
  }

  function dispose() {
    inputProvider.dispose?.();
  }

  return {
    update,
    dispose,
    // ポーズ中に入力を止める。入力プロバイダが満たすべき約束は3つ:
    //  1. setEnabled(false) で、掴んでいる入力（押しっぱなしのキー・ドラッグ中の
    //     スティック）をすべて手放す。再開直後に暴走しないための本体。
    //  2. 無効中はブラウザ既定の動作を奪わない（keydown の preventDefault を
    //     止める）。モーダル上の Space による決定や矢印キーのスクロールが死ぬ。
    //  3. 無効中でも「離した」ことは観測する（keyup は記録する）。
    setEnabled(on) { inputProvider.setEnabled?.(on); },
    get speed() { return state.speed; },
    get rotateSpeed() { return ROTATE_SPEED_MS; },
    get noseUpCommanded() { return state.noseUpCommanded; },
  };
}
